/**
 * The plan decision, asked the way a person is asked anything: the plan, Weave's open question if it has one, then three numbered
 * choices — 1 Approve, 2 Decline, 3 a text box (Enter sends). Labels only: no descriptions, no helper lines, nothing that competes
 * (dev 2026-11-02: questions buried under the items, then a Continue button beside two links, read as a form).
 *
 * Approve / Decline answer this card exactly as its buttons always did (`option_id`). The text box sends a chat message — an answer to
 * Weave's question or a change to the plan — quoting the open question when there is one, so the reply cannot be read as any other
 * instruction; the server revises the plan and it comes back on a new card.
 *
 * Same paint as `InteractionCard`: the left rule carries the meaning, no box (see that component's "NOT A CARD" note).
 */
import { useId, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface PlanQuestionsCardProps {
  /** Weave's open questions on this plan (may be empty). */
  questions: readonly string[];
  /** The plan being decided (goal + items). */
  plan: ReactNode;
  /** Send the typed text as a chat message. */
  onSend: (message: string) => void;
  /** Approve or decline this card (`option_id`). */
  onChoose: (optionId: "approve" | "decline") => Promise<void> | void;
  error?: string | null;
  className?: string;
}

/** What the text box sends: with one open question, the text is its answer, quoted with it; otherwise it is said as written. */
export function somethingElseMessage(questions: readonly string[], text: string): string {
  const t = text.trim();
  if (questions.length === 1) return `Answer to the plan's open question "${questions[0].trim()}": ${t}`;
  if (questions.length > 1) return `About the plan's open questions (${questions.map((q) => `"${q.trim()}"`).join("; ")}): ${t}`;
  return t;
}

/** One choice row: a clear hover (border + fill + a small lift of the number), a visible keyboard focus, a pressed state. */
/**
 * How the person's own bubble shows a message this card sent (dev 2026-11-02: the bubble read "Answer to the plan's open question
 * "Should 'Rejected'…": Only…"). Only the exact wording `somethingElseMessage` writes is recognised — this module owns both ends —
 * so nothing a person typed is ever rewritten. The server still receives the full, quoted message.
 */
export function answerDisplay(text: string): string | null {
  const one = /^Answer to the plan's open question "([\s\S]+)": ([\s\S]+)$/.exec(text.trim());
  if (one) return `_Answer to: ${one[1].trim()}_\n\n${one[2].trim()}`;
  const many = /^About the plan's open questions \(([\s\S]+)\): ([\s\S]+)$/.exec(text.trim());
  if (many) return `_Answer to Weave's questions_\n\n${many[2].trim()}`;
  return null;
}

const ROW =
  "group flex h-10 w-full cursor-pointer items-center gap-3 rounded-md border px-3 text-left text-[13.5px] font-medium transition-[background-color,border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background active:scale-[0.995] disabled:cursor-not-allowed disabled:opacity-60";
/** 1 Approve — the one filled choice, in the composer Send's own blue and white ink (`--ma-send-*`), so "go" looks the same everywhere. */
const ROW_PRIMARY = "border-transparent bg-[var(--ma-send-bg)] text-[var(--ma-send-ink)] shadow-sm hover:brightness-110 hover:shadow";
/**
 * 2 Decline and 3 type — quiet until pointed at. The hover fill is written `bg-[hsl(var(--muted)/0.6)]`, NOT `bg-muted/60`: the studio
 * shell forces a grey `!important` border on any element whose classes contain both "bg-muted/" and "border" (globals.css), which
 * silently erased the type row's focus border (dev 2026-11-02, found with the browser's matched-rules list).
 */
const ROW_QUIET = "border-border bg-transparent text-foreground hover:border-foreground/25 hover:bg-[hsl(var(--muted)/0.6)]";

function Num({ n, primary = false }: { n: number; primary?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded text-[12px] font-semibold tabular-nums transition-colors",
        primary ? "bg-white/20 text-[var(--ma-send-ink)]" : "bg-muted text-muted-foreground group-hover:bg-background group-hover:text-foreground",
      )}
    >
      {n}
    </span>
  );
}

export function PlanQuestionsCard({ questions, plan, onSend, onChoose, error, className }: PlanQuestionsCardProps) {
  const titleId = useId();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<null | "approve" | "decline">(null);
  const choose = async (id: "approve" | "decline") => {
    setBusy(id);
    try {
      await onChoose(id);
    } finally {
      setBusy(null);
    }
  };
  const send = () => {
    if (!text.trim() || busy) return;
    onSend(somethingElseMessage(questions, text));
    setText("");
  };
  const placeholder = questions.length ? "Type your answer or a change…" : "Type a change…";
  return (
    <section aria-labelledby={titleId} className={cn("border-l-2 border-l-primary py-1 pl-3.5", className)} data-lane-v2-plan-questions="">
      <h4 id={titleId} className="text-[14.5px] font-semibold leading-snug text-foreground">
        Approve this plan?
      </h4>
      <div className="mt-1.5">{plan}</div>

      {questions.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1" data-lane-v2-open-questions="">
          {questions.map((q, i) => (
            <li key={i} className="flex gap-2 text-[13.5px] leading-snug text-foreground">
              <span aria-hidden className="shrink-0 font-semibold text-[var(--ma-state-warn-text)]">?</span>
              <span className="min-w-0">
                <span className="sr-only">Weave asks: </span>
                {q}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex max-w-[36rem] flex-col gap-1.5" role="group" aria-label="Your choice">
        <button type="button" className={cn(ROW, ROW_PRIMARY)} disabled={!!busy} onClick={() => void choose("approve")} data-interaction-option="approve">
          <Num n={1} primary />
          {busy === "approve" ? "Approving…" : "Approve"}
        </button>
        <button type="button" className={cn(ROW, ROW_QUIET)} disabled={!!busy} onClick={() => void choose("decline")} data-interaction-option="decline">
          <Num n={2} />
          {busy === "decline" ? "Declining…" : "Decline"}
        </button>
        <form
          // `ma-choice-field`: the row shows focus (border + a soft OUTLINE ring: the studio shell erases box-shadows), its field draws none
          className={cn(ROW, ROW_QUIET, "ma-choice-field cursor-text pr-1.5 focus-within:border-[color:var(--ma-send-bg)] focus-within:bg-transparent focus-within:[outline:3px_solid_rgba(11,96,234,0.22)] focus-within:[outline-offset:0px]")}
          data-lane-v2-something-else=""
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <Num n={3} />
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={!!busy}
            placeholder={placeholder}
            aria-label={placeholder}
            className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[13.5px] font-normal text-foreground shadow-none outline-none ring-0 placeholder:text-muted-foreground focus:outline-none focus:ring-0"
          />
          <button
            type="submit"
            disabled={!text.trim() || !!busy}
            aria-label="Send"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[var(--ma-send-bg)] text-[14px] font-semibold text-[var(--ma-send-ink)] transition-[filter,background] hover:brightness-110 disabled:bg-muted disabled:text-muted-foreground disabled:hover:brightness-100"
          >
            ↵
          </button>
        </form>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-[12.5px] text-ma-err-text">
          {error}
        </p>
      )}
    </section>
  );
}
